import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

// Cliente da edge convert-link (geracao REAL de link de afiliado pelo painel).
// Contrato espelha supabase/functions/convert-link/convert.ts e bulk-run.ts.

export interface ProductInfo {
  readonly title: string | null;
  readonly imageUrl: string | null;
  readonly priceCents: number | null;
}

export interface ConversionResult {
  readonly status: "converted" | "error" | "ignored";
  readonly inputUrl: string;
  readonly resolvedUrl: string;
  readonly store: string | null;
  readonly finalUrl?: string;
  readonly affiliateUrl?: string;
  readonly linkType?: "short" | "direct";
  readonly product?: ProductInfo | null;
  readonly linkId?: string | null;
  readonly errorCode?: string;
  readonly message?: string;
}

export interface BulkSummary {
  readonly total: number;
  readonly converted: number;
  readonly errors: number;
  readonly ignored: number;
}

export interface BulkResponse {
  readonly items: readonly ConversionResult[];
  readonly summary: BulkSummary;
}

export interface ValidationOutcome {
  readonly status: "valid" | "invalid" | "unchanged";
  readonly needsReauth: boolean;
  readonly message: string | null;
}

/** Erro de chamada (rede, auth, payload) com codigo legivel pela UI. */
export class ConvertLinkError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = "ConvertLinkError";
    this.code = code;
  }
}

async function readErrorCode(invokeError: unknown): Promise<string> {
  if (!(invokeError instanceof FunctionsHttpError)) {
    return "network_error";
  }
  try {
    const body = (await invokeError.context.json()) as { error?: string };
    return body.error ?? "provider_error";
  } catch {
    return "provider_error";
  }
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>("convert-link", { body });
  if (error) {
    throw new ConvertLinkError(await readErrorCode(error));
  }
  if (!data) {
    throw new ConvertLinkError("empty_response");
  }
  return data;
}

export function convertLink(url: string, shorten: boolean): Promise<ConversionResult> {
  return invoke<ConversionResult>({ action: "convert", url, shorten });
}

export function convertBulk(text: string, shorten: boolean): Promise<BulkResponse> {
  return invoke<BulkResponse>({ action: "bulk", text, shorten });
}

/** Encurtador avulso pela edge (blocklist de lojas e destino resolvido no servidor). */
export function shortenUrl(url: string): Promise<{ readonly ok: true; readonly shortUrl: string }> {
  return invoke<{ ok: true; shortUrl: string }>({ action: "shorten", url });
}

export function validateCredential(marketplace: string): Promise<ValidationOutcome> {
  return invoke<ValidationOutcome>({ action: "validate", marketplace });
}

const CALL_ERROR_LABELS: Readonly<Record<string, string>> = {
  network_error: "Sem conexão com o servidor. Verifique a internet e tente de novo.",
  missing_authorization: "Sua sessão expirou. Entre de novo no painel.",
  tenant_not_found: "Conta não encontrada. Entre de novo no painel.",
  invalid_url: "Esse link não parece válido.",
  invalid_text: "Cole um texto com pelo menos um link (até 20 mil caracteres).",
  credential_missing: "Salve a credencial antes de testar.",
  rate_limited: "Muitos links em pouco tempo. Aguarde um minuto e tente de novo.",
  rate_limit_unavailable: "Não conseguimos checar seu limite agora. Tente de novo em instantes.",
  invalid_feed_request: "Busca inválida. Use até 100 caracteres.",
  provider_unavailable: "A loja não respondeu agora. Tente de novo em instantes.",
};

/** Mensagem pt-BR para falha da chamada em si (nao da conversao). */
export function callErrorMessage(error: unknown): string {
  const code = error instanceof ConvertLinkError ? error.code : "provider_error";
  return CALL_ERROR_LABELS[code] ?? "Não foi possível falar com o servidor agora. Tente de novo.";
}

// ---------------------------------------------------------------------------
// Feed de ofertas (acao "offers" da mesma edge). Contrato espelha
// supabase/functions/convert-link/offers.ts.
// ---------------------------------------------------------------------------

export type FeedStore = "shopee" | "amazon" | "mercadolivre";

export interface FeedOffer {
  readonly itemId: string;
  readonly title: string;
  readonly imageUrl: string | null;
  readonly priceCents: number | null;
  readonly originalPriceCents: number | null;
  readonly discountPercent: number | null;
  readonly commissionPercent: number | null;
  readonly sales: number | null;
  readonly rating: number | null;
  readonly productUrl: string;
  readonly offerUrl: string | null;
}

export type FeedResponse =
  | { readonly status: "ok"; readonly store: FeedStore; readonly offers: readonly FeedOffer[]; readonly cached: boolean }
  | {
      readonly status: "credential_missing" | "credential_incomplete" | "credential_rejected" | "unavailable";
      readonly store: FeedStore;
      readonly message: string;
    };

export interface FeedSearch {
  readonly store: FeedStore;
  readonly keyword: string;
  readonly page: number;
}

export function searchOffers(search: FeedSearch): Promise<FeedResponse> {
  return invoke<FeedResponse>({ action: "offers", ...search });
}
